import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listCompanies } from '@/api/companies'
import { CompanyTable } from '@/components/companies/CompanyTable'
import { CompanyDrawer } from '@/components/companies/CompanyDrawer'
import { Button } from '@/components/ui/Button'
import { PillToggle } from '@/components/ui/PillToggle'
import { SearchInput } from '@/components/ui/SearchInput'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

export function CompaniesPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [inactive, setInactive] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const debouncedSearch = useDebouncedValue(search, 250)

  const { data, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['companies', debouncedSearch, inactive],
    queryFn: () =>
      listCompanies({
        page: 1,
        pageSize: 100,
        type: 'all',
        search: debouncedSearch,
        includeInactive: inactive,
      }),
    placeholderData: keepPreviousData,
  })

  const companies = data?.items ?? []
  const tableLoading = isFetching && (isPlaceholderData || !data)

  return (
    <div>
      <PageHeader
        title={t('companies.title')}
        actions={
          <Button variant="primary" onClick={() => setDrawerOpen(true)}>
            {t('companies.create')}
          </Button>
        }
        filters={
          <>
            <SearchInput
              placeholder={t('companies.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <PillToggle
              checked={inactive}
              onChange={setInactive}
              label={t('companies.showInactive')}
            />
          </>
        }
      />
      <CompanyTable
        companies={companies}
        loading={tableLoading}
        onRowClick={(id) => navigate(`/companies/${id}`)}
      />
      <CompanyDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => {
          void qc.invalidateQueries({ queryKey: ['companies'] })
        }}
        onCreated={(companyId) => navigate(`/companies/${companyId}`)}
      />
    </div>
  )
}
